import { useEffect, useMemo, type KeyboardEvent, type FormEvent } from "react";
import { useForm, Controller } from "react-hook-form";
import type { Control } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { combineClasses, requiredMessage } from "@/lib/utils";
import { toOffsetDateTime, todayInputValue } from "@/lib/dateTime";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChapters } from "@/store/slices/chapterSlice";
import EventImageUpload from "./EventImageUpload/EventImageUpload";
import styles from "./AddPastEventModal.module.scss";
import type { CreatePastEventPayload } from "@/services/api/pastEvents";

export interface SelectOption {
  label: string;
  value: number | string;
}

export enum PastEventType {
    MARQUEE = "MARQUEE",
    MICRO_MEETUPS = "MICRO_MEETUPS",
    MASTERCLASS = "MASTERCLASS"
}
const DEFAULT_CHAPTER: SelectOption = { label: "Mumbai", value: 1 };

const DEFAULT_CHAPTER_OPTIONS: SelectOption[] = [
  DEFAULT_CHAPTER,
  { label: "Bangalore", value: 2 },
  { label: "Delhi NCR", value: 3 },
  { label: "Pune", value: 4 },
];

const EVENT_TYPE_OPTIONS: SelectOption[] = [
  { label: "Marquee", value: PastEventType.MARQUEE },
  { label: "Micro Meetups", value: PastEventType.MICRO_MEETUPS },
  { label: "Masterclass", value: PastEventType.MASTERCLASS },
];

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const TITLE_FIELD: FormConfig = {
  name: {
    controlType: "input",
    label: "Event title",
    isRequired: true,
    labelVariant: "caption",
    placeholder: "e.g. The Old Money Gathering",
    validations: {
      required: requiredMessage("Event title"),
      validate: (value: string) => (value ?? "").trim().length > 0 || requiredMessage("Event title"),
      maxLength: { value: 255, message: "Event title must be under 255 characters" },
    },
    props: { className: combineClasses(styles.fieldInput) },
  },
};

const WRITE_UP_FIELD: FormConfig = {
  writeUp: {
    controlType: "textarea",
    label: "Write-up",
    isRequired: true,
    labelVariant: "caption",
    placeholder: "A short account of the evening, shown under the photos.",
    validations: {
      required: requiredMessage("Write-up"),
      validate: (value: string) => (value ?? "").trim().length > 0 || requiredMessage("Write-up"),
      maxLength: { value: 2000, message: "Write-up cannot exceed 2000 characters" },
    },
    props: { className: combineClasses(styles.fieldTextarea), rows: 3 },
  },
};

export interface AddPastEventFormValues {
  name: string;
  city: SelectOption | null;
  date: string;
  attendance: SelectOption | string | number | null;
  writeUp: string;
  file: FileList | File | null;
  eventType:SelectOption |PastEventType |null;
}

export interface AddPastEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (payload: FormData) => Promise<void>;
}

const AddPastEventModal = ({ isOpen, onClose, onCreate }: AddPastEventModalProps) => {
  const dispatch = useAppDispatch();
  const chapters = useAppSelector((state) => state.chapter.chapters);

  useEffect(() => {
    if (isOpen) {
      dispatch(getChapters({ pageNo: 1, perPage: 100 }));
    }
  }, [dispatch, isOpen]);

  const chapterOptions: SelectOption[] = useMemo(() => {
    if (!chapters || chapters.length === 0) return DEFAULT_CHAPTER_OPTIONS;
    return chapters.map((chapter) => ({ label: chapter.name, value: chapter.id }));
  }, [chapters]);

  const defaultCity: SelectOption = chapterOptions[0] ?? DEFAULT_CHAPTER;

  const { register, handleSubmit, control, reset, setValue, watch, formState } = useForm<AddPastEventFormValues>({
    defaultValues: {
      name: "",
      city: defaultCity,
      date: "",
      attendance: null,
      writeUp: "",
      file: null,
      eventType:null
    },
  });

  const city = watch("city");
  useEffect(() => {
    const firstChapter = chapters?.[0];
    if (firstChapter) {
      if (!city || city.value === DEFAULT_CHAPTER.value) {
        setValue("city", { label: firstChapter.name, value: firstChapter.id });
      }
    }
  }, [chapters, city, setValue]);

  const row1Fields: FormConfig = useMemo(
    () => ({
      city: {
        controlType: "select",
        label: "City / Chapter",
        isRequired: true,
        labelVariant: "caption",
        isControlled: true,
        validations: { required: requiredMessage("City / Chapter") },
        props: { options: chapterOptions, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      },
      date: {
        controlType: "date",
        label: "Date held",
        isRequired: true,
        labelVariant: "caption",
        isControlled: true,
        placeholder: "Enter Date",
        // Unlike CreateGathering's `date` (future-only, `min: today`), this
        // is logging something that already happened — so the same
        // "today" boundary applies the other way, as a `max` plus a
        // `validate` (the native `max` attribute alone doesn't stop a
        // typed/pasted value, only the date-picker UI).
        validations: {
          required: requiredMessage("Date held"),
          validate: (value: string) => {
            if (!value) return requiredMessage("Date held");
            const selected = new Date(value);
            if (Number.isNaN(selected.getTime())) return true;
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);
            return selected <= endOfToday || "Date held can't be in the future";
          },
        },
        props: { className: combineClasses(styles.fieldInput), max: todayInputValue() },
      },
    }),
    [chapterOptions],
  );

  const row2Fields: FormConfig = useMemo(
    () => ({
      attendance: {
        controlType: "input",
        label: "Attendance",
        labelVariant: "caption",
        isControlled: true,
        validations: {
          maxLength: {
            value: 5,
            message: "Attendance must be at most 5 digits",
          },
          validate: (value: unknown) => {
            const raw =
              typeof value === "object" && value !== null ? (value as SelectOption).value : value;
            const trimmed = String(raw ?? "").trim();
            if (trimmed === "") return true;
            if (!/^\d+$/.test(trimmed)) return "Attendance must be a whole number";
            if (trimmed.length > 5) return "Attendance must be at most 5 digits";
            const num = Number(trimmed);
            if (!Number.isFinite(num) || !Number.isInteger(num)) return "Attendance must be a whole number";
            return num >= 0 || "Attendance cannot be negative";
          },
        },
        props: {
          type: "text",
          inputMode: "numeric",
          maxLength: 5,
          className: combineClasses(styles.fieldInput),
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
            if (
              !/^\d$/.test(e.key) &&
              !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter"].includes(e.key) &&
              !e.ctrlKey &&
              !e.metaKey
            ) {
              e.preventDefault();
            }
          },
          onInput: (e: FormEvent<HTMLInputElement>) => {
            const target = e.currentTarget;
            const digitsOnly = target.value.replace(/\D/g, "").slice(0, 5);
            if (target.value !== digitsOnly) {
              target.value = digitsOnly;
            }
          },
        },
      },
      eventType:{
        controlType:"select",
        label:"Event Type",
        labelVariant:"caption",
        isRequired:true,
        validations:{required:requiredMessage("Event Type")},
        isControlled:true,
        props: { options: EVENT_TYPE_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
      }
    }),
    [],
  );

  const handleClose = () => {
    reset({
      name: "",
      city: chapterOptions[0] ?? DEFAULT_CHAPTER,
      date: "",
      attendance: null,
      writeUp: "",
      file: null,
      eventType:null
    });
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const attendanceRaw =
        typeof values.attendance === "object" && values.attendance !== null
          ? (values.attendance as any).value
          : values.attendance;

      const eventTypeRaw =
        typeof values.eventType === "object" && values.eventType !== null
          ? values.eventType.value
          : values.eventType;

      const attendanceText = String(attendanceRaw ?? "").trim();

      const payload: CreatePastEventPayload = {
        name: values.name.trim(),
        description: values.writeUp ? values.writeUp.trim() : "",
        cityId: Number(values.city?.value),
        // The picked day at local midnight, e.g. "2026-09-30T00:00:00+05:30".
        eventDate: toOffsetDateTime(values.date) ?? values.date,
        ...(attendanceText !== "" ? { attended: Number(attendanceText) } : {}),
        ...(eventTypeRaw ? { eventType: String(eventTypeRaw) } : {}),
      };

      // Multipart endpoint: the backend binds each DTO field directly from
      // the form parts, so append them individually (not as a JSON blob).
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("description", payload.description);
      formData.append("cityId", String(values.city?.value ?? ""));
      formData.append("eventDate", payload.eventDate);
      if (payload.attended !== undefined) {
        formData.append("attended", String(payload.attended));
      }
      if (payload.eventType) {
        formData.append("eventType", payload.eventType);
      }

      const fileList = values.file as unknown as FileList | File | null;
      let file: File | null = null;
      if (fileList instanceof File) {
        file = fileList;
      } else if (fileList && typeof fileList === "object" && 0 in fileList) {
        file = (fileList as unknown as FileList)[0] ?? null;
      }

      if (file) {
        formData.append("file", file);
      }

      await onCreate(formData);
      handleClose();
    } catch {
      // If the API fails, keep modal open. Error toast is displayed by onCreate.
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add a past event"
      subtitle="Photos and the write-up members will see in the app."
      onSubmit={onSubmit}
      submitLabel="Create event"
      submitIcon={<PlusIcon width={14} height={14} />}
      submitDisabled={formState.isSubmitting}
      className={combineClasses(styles.addPastEventModal)}
    >
      {formRenderer<AddPastEventFormValues>(TITLE_FIELD, formState, register, control)}

      <div className={styles.fieldGrid}>
        {formRenderer<AddPastEventFormValues>(row1Fields, formState, register, control)}
      </div>

      <div className={styles.fieldGrid}>
        {formRenderer<AddPastEventFormValues>(row2Fields, formState, register, control)}
      </div>

      {formRenderer<AddPastEventFormValues>(WRITE_UP_FIELD, formState, register, control)}
      <Controller
        control={control}
        name="file"
        rules={{
          required: requiredMessage("Event image"),
          validate: (value) => {
            if (!value) {
              return requiredMessage("Event image");
            }
            if (value instanceof File && value.size > MAX_IMAGE_SIZE_BYTES) {
              return "Event image must be 5MB or smaller";
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <EventImageUpload
            file={field.value instanceof File ? field.value : null}
            onChange={(selectedFile) => field.onChange(selectedFile)}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
            disabled={formState.isSubmitting}
          />
        )}
      />
    </FormModal>
  );
};

export default AddPastEventModal;
